import { useQueryParams } from '@hooks/queryParams';
import { addDays, isToday, subDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { UseDateProps } from './types';
import { formatDate, parseTenantDate } from '@utils/dateHelpers';
import { TZDate } from '@date-fns/tz';
import {
  getTimeZoneOf,
  inTenantZone,
  tenantNow,
  toTenantDate,
  toTenantWallClock,
} from '@utils/timeZone';
import { useAppContext } from '@contexts/app';

export const useDate = (options: UseDateProps = {}) => {
  const { initialDate, disableLoadFromParams, disableParamsUpdate } = options;
  const { params, setParam } = useQueryParams();
  const {
    tenant: { zone },
  } = useAppContext();

  const getInitialDate = () =>
    params?.date && !disableLoadFromParams
      ? parseTenantDate(params.date)
      : toTenantDate(initialDate || tenantNow());

  const isInZone = (value: Date) => !zone || getTimeZoneOf(value) === zone;

  const [state, setState] = useState<Date>(getInitialDate);

  const rawDate = useMemo(
    () => {
      if (isInZone(state)) return state;
      return state instanceof TZDate ? getInitialDate() : toTenantWallClock(state);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, zone],
  );

  useEffect(() => {
    if (rawDate !== state) setState(rawDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDate]);

  const [debounced] = useDebounce(rawDate, 500);
  const date = isInZone(debounced) ? debounced : rawDate;

  const handlePrevDay = () => setState((prev) => subDays(prev, 1));
  const handleNextDay = () => setState((prev) => addDays(prev, 1));

  useEffect(() => {
    if (disableParamsUpdate) return;
    if (!date) return;
    if (isToday(date, { in: inTenantZone() }) && !params?.date) return;
    setParam('date', formatDate(date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return { date, rawDate, setRawDate: setState, handlePrevDay, handleNextDay };
};
